import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';

const VerificationBanner = styled.div`
  background: linear-gradient(135deg, var(--accent), var(--accent-strong));
  color: #212529;
  padding: 0.75rem 1rem;
  text-align: center;
  font-size: 0.9rem;
  font-weight: 500;
  border-bottom: 2px solid #e9ecef;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const BannerContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  position: relative;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
`;

const MainContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  flex: 1;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
    margin-top: 1rem;
  }
`;

const BannerText = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ResendButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #212529;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #212529;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  opacity: 0.7;
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;

  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.2);
  }

  @media (max-width: 768px) {
    position: absolute;
    top: 0.25rem;
    right: 0.25rem;
  }
`;

const EmailVerificationBanner: React.FC = () => {
  const { user, isAuthenticated, isLoading, resendVerification, refreshProfile } = useAuth();
  const location = useLocation();
  const [isHidden, setIsHidden] = useState(false);
  const [isResending, setIsResending] = useState(false);

  // Note: Removed automatic periodic refresh to prevent infinite loops
  // Users can manually refresh using the refresh button if needed

  // Pages where banner should not be shown
  const hiddenOnPages = ['/settings', '/verify'];
  const shouldHideBanner = hiddenOnPages.some(page => 
    location.pathname.startsWith(page)
  );

  // Don't show banner if still loading, user is not authenticated, is verified, banner is hidden, or on certain pages
  if (isLoading || !isAuthenticated || !user || user.verified || isHidden || shouldHideBanner) {
    return null;
  }

  const handleResend = async () => {
    setIsResending(true);
    await resendVerification();
    setIsResending(false);
  };

  const handleRefresh = async () => {
    console.log('🔄 Manual refresh triggered by user');
    await refreshProfile({ force: true });
  };

  return (
    <VerificationBanner>
      <BannerContent>
        <CloseButton 
          onClick={() => setIsHidden(true)}
          title="Dismiss this banner"
          aria-label="Dismiss verification banner"
        >
          ×
        </CloseButton>
        <MainContent>
          <BannerText>
            ⚠️ Please verify your email address to receive notifications and access all features.
          </BannerText>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <ResendButton 
              onClick={handleResend} 
              disabled={isResending}
            >
              {isResending ? 'Sending...' : 'Resend Verification Email'}
            </ResendButton>
            <ResendButton 
              onClick={handleRefresh}
              style={{ background: '#28a745', fontSize: '12px', padding: '6px 12px' }}
              title="Click to check if your email has been verified"
            >
              ✅ Check Verification Status
            </ResendButton>
          </div>
        </MainContent>
      </BannerContent>
    </VerificationBanner>
  );
};

export default EmailVerificationBanner;
